<script setup lang="ts">
// Define proper TypeScript interface for testimonial data structure
interface Testimonial {
  description: string;
  authName: string;
  authRole: string;
  companyName?: string;
  authImg: string;
}

// Extract testimonial content to avoid repetition
const testimonialContent = {
  description:
    "We have chosen WebReinvent Technologies after a long research. We have cross checked capabilities and reviews of many vendors and finally awarded our projects to WebReinvent. I can proudly say that my decision was correct.",
  authName: "Zack Rinkins",
  authRole: "CEO",
  companyName: "@Lorem",
};

const testimonials = ref<Testimonial[]>([
  {
    description:
      "We chose WebReinvent after much research. They gave value to my business and were sincere throughout the project lifecycle.",
    authName: "Zack Rinkins",
    authRole: "CEO",
    authImg: "/images/zack.webp",
  },
  {
    description:
      "From day one, the team was friendly and helpful. They not only build apps but also train you well to use them effectively.",
    authName: "Tiffani A. Gilbert",
    authRole: "CEO",
    authImg: "/images/taffain.webp",
  },
  {
    description:
      "Thanks for fast and efficient delivery. We’re impressed with WebReinvent’s service and will definitely recommend them.",
    authName: "Ankur Shah",
    authRole: "CEO",
    authImg: "/images/ankur.webp",
  },
  {
    description:
      "They appeared as the ultimate solution, bringing brilliant ideas in a tight deadline and making everything work smoothly.",
    authName: "Pranjal",
    authRole: "CEO",
    authImg: "/images/pranjal.webp",
  },
  {
    description:
      "WebReinvent is a highly skilled team with an eye for detail. Project management and communication worked really well.",
    authName: "Jeff Crystal",
    authRole: "CEO",
    authImg: "/images/jeffCrystal.webp",
  },
  {
    description:
      "WebReinvent is a skilled team with attention to detail. We got exactly what we wanted and more through great project tools.",
    authName: "Daniel Bates",
    authRole: "CEO",
    authImg: "/images/danielBates.webp",
  },
  {
    description:
      "Thank you for a great job on our website. The brilliant design you created gave our company a professional new look.",
    authName: "Pawan Shah",
    authRole: "CEO",
    authImg: "/images/pawanShah.webp",
  },
  {
    description:
      "I’ve worked with WebReinvent for over a year. They are timely, professional, and creative in enhancing my site ideas.",
    authName: "Darrell Bennett",
    authRole: "CEO",
    authImg: "/images/darrellBennett.webp",
  },
  {
    description:
      "Working with WebReinvent was pleasant. They responded quickly and clearly understood the project scope, delivering well.",
    authName: "Geoffrey Nwachukwu",
    authRole: "CEO",
    authImg: "/images/geoffreyNwachukwu.webp",
  },
]);

// Use computed properties to handle responsive behavior instead of direct DOM manipulation
const isMobile = ref(false);

// Use throttle/debounce for resize events to prevent performance issues
let resizeTimeout: number | null = null;

// Improve breakpoint definitions with a more descriptive name
const carouselBreakpoints = ref([
  { breakpoint: "1400px", numVisible: 3, numScroll: 1 }, // For extra-large screens
  { breakpoint: "1200px", numVisible: 3, numScroll: 1 }, // For large screens
  { breakpoint: "992px", numVisible: 2, numScroll: 1 }, // For medium screens
  { breakpoint: "768px", numVisible: 2, numScroll: 1 }, // For smaller tablets
  { breakpoint: "700px", numVisible: 1, numScroll: 1 }, // For mobile screens
]);

// Improve the resize handler with debouncing for better performance
function handleResize() {
  if (resizeTimeout) {
    window.clearTimeout(resizeTimeout);
  }

  resizeTimeout = window.setTimeout(() => {
    isMobile.value = window.innerWidth <= 768;
  }, 150); // Debounce by 150ms
}

// Properly handle component lifecycle
onMounted(() => {
  // Set initial state
  isMobile.value = window.innerWidth <= 768;
  // Add event listener with debounced handler
  window.addEventListener("resize", handleResize);
});

// Clean up event listeners to prevent memory leaks
onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  // Clear any pending timeouts
  if (resizeTimeout) {
    window.clearTimeout(resizeTimeout);
  }
});

// Common styling classes extracted as constants for better maintainability
const carouselItemClasses =
  "mx-1 sm:mx-4 h-full  c-carousel bg-[linear-gradient(140.57deg,#441067C7_10.98%,#221E69C7_93.74%)] overflow-hidden rounded-2xl";
</script>

<template>
  <section class="max-screen-xl px-4">
  <Carousel
    :value="testimonials"
    :showNavigators="!isMobile"
    :numVisible="3"
    :numScroll="1"
    :responsiveOptions="carouselBreakpoints"
    :autoplayInterval="5000"
    circular
    :dragEnabled="true"
    touchEnabled
    :pt="{
      itemList: { class: '' },
      item: {
        class:
          '  *:even:bg-[image:var(--bg-card-even)] *:odd:bg-[image:var(--bg-card)]',
      },
      indicatorbutton: {
        class:
          '!rounded-full !size-4 !border !border-[#6727BB] !border-solid !bg-transparent',
      },
      content: { class: '' },
    }"
  >
    <template #item="slotProps">
      <div :class="carouselItemClasses">
        <Card
          unstyled
          :class="[
            'h-full relative overflow-hidden px-8 py-4 text-white after:content-[\'\'] after:hidden after:absolute after:right-10 after:-top-40 after:w-[65px] after:h-[400px] after:rotate-[30deg] after:blur-[50px]',
            slotProps.index % 2 === 0
              ? 'after:bg-[linear-gradient(180.37deg,#A066C7_29.35%,#3B0C5B_131.96%)]'
              : 'after:bg-[linear-gradient(180.37deg,#737CD3_29.35%,#1B0742_131.96%)]',
          ]"
          :pt="{
            content: { class: 'z-10 relative h-full flex flex-col' },
            body: { class: 'h-full' },
          }"
        >
          <template #content>
            <p class="text-sm tracking-[0.5px]">
              {{ slotProps.data.description }}
            </p>
            <div class="flex items-center gap-4 mt-4">
              <div
                class="p-px bg-[linear-gradient(180deg,#F5F2FF_0%,#C4C2E0_100%)] rounded-full"
              >
                <NuxtImg
                  :alt="`${slotProps.data.authName} profile image`"
                  :src="slotProps.data.authImg"
                  width="64"
                  height="64"
                  loading="lazy"
                  class="rounded-full p-[2px] bg-[image:var(--border-testimonial-card)]"
                  format="webp"
                />
              </div>
              <div>
                <p class="tracking-[0.5px]">{{ slotProps.data.authName }}</p>
                <p
                  class="tracking-[0.5px]"
                  :class="
                    slotProps.index % 2 === 0
                      ? 'text-[var(--text-testimonial-card-even)]'
                      : 'text-[var(--text-testimonial-card)]'
                  "
                >
                  {{ slotProps.data.authRole }}
                  <span class="underline tracking-[0.5px]">{{
                    slotProps.data.companyName
                  }}</span>
                </p>
              </div>
            </div>
          </template>
        </Card>
      </div>
    </template>
  </Carousel>
  </section>
</template>
